// One identity's possible matches: the star and the candidate table, wired to the
// detail queries of PRD-0030. A candidate opens the document detail overlay on
// its face, focused on this identity (PRD-0031 slice 08). A candidate whose face
// another identity already owns is merged, not confirmed — after the merge
// dialog asks, and lets the analyst pick which of the two stays (ADR-0042,
// ADR-0052). A viewer decides none of it (PRD-0032 SC-036): the read names the
// caller's role, and the star's panel, the table and the reference strip then
// offer nothing to decide.
//
// Both places that show them mount this: the *Photo matching* tab and the
// identity page (PRD-0031 slice 02). Nothing is copied — the tab kept the
// composition it always had, it just lives here now, and the identity page
// passes the person it is already showing.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDocumentOverlayOptional } from '@/components/document-overlay/overlay-context'
import { FaceActionNotice } from '@/components/faces/FaceActionNotice'
import { FaceCandidateTable } from '@/components/faces/FaceCandidateTable'
import { FaceReferenceStrip } from '@/components/faces/FaceReferenceStrip'
import { MergeIdentitiesDialog } from '@/components/faces/MergeIdentitiesDialog'
import { Skeleton } from '@/components/ui/skeleton'
import { identityPath } from '@/features/identities/identity-path'
import { canDecideFaces } from './decision-role'
import { useCandidateDecisions } from './hooks/useCandidateDecisions'
import {
  CANDIDATES_PAGE_SIZE,
  identityQuery,
  STAR_DETAIL_OPTIONS,
  useFaceMatchingIdentity,
} from './hooks/useFaceMatching'
import { starMergePair } from './merge-pair'
import { PhotoMatchingStar } from './PhotoMatchingStar'

export interface IdentityMatchesProps {
  caseId: string
  /** The identity to show; null renders nothing, so the tab may mount it unselected. */
  personId: string | null
  /**
   * Show the reference strip above the star. The tab does; the identity page
   * has the faces card of slice 01 for the same faces.
   */
  references?: boolean
  /**
   * Run after every decision and merge, on top of the face-matching queries the
   * mutations refresh themselves — the identity page re-reads its own detail,
   * because confirming a face can move the main photo and a merge brings the
   * merged identity's documents and relations. Not run after a merge that folded
   * this identity into the other one (ADR-0052): nothing of it is left to
   * re-read, and whoever mounts this follows the survivor (`useMergedInto`).
   */
  onDecided?: () => void
}

/** The filter and page of one identity's table. */
interface MatchesView {
  personId: string | null
  suggestedOnly: boolean
  page: number
}

const freshView = (personId: string | null): MatchesView => ({
  personId,
  suggestedOnly: false,
  page: 1,
})

const BASE = 'cases.investigate.entityResolution.photoMatching'

export function IdentityMatches({
  caseId,
  personId,
  references = false,
  onDecided,
}: IdentityMatchesProps) {
  const { t } = useTranslation()
  const [stored, setStored] = useState<MatchesView>(() => freshView(personId))
  // Another identity is another table: its filter and its page start over,
  // without waiting for an effect to catch up.
  const view = stored.personId === personId ? stored : freshView(personId)
  if (view !== stored) setStored(view)

  // Optional on purpose: the composition is mounted inside the case shell, which
  // owns the overlay — outside it (a story, a unit render) a candidate simply does
  // not open.
  const overlay = useDocumentOverlayOptional()
  const tableState = { suggestedOnly: view.suggestedOnly, page: view.page }
  const detail = useFaceMatchingIdentity(caseId, personId, tableState)
  // The star reads its own query, so filtering or paging the table never hides
  // the identity's strongest suggestions from it (SC-007).
  const star = useFaceMatchingIdentity(caseId, personId, STAR_DETAIL_OPTIONS)
  const cached = [identityQuery(tableState), identityQuery(STAR_DETAIL_OPTIONS)]
  const decisions = useCandidateDecisions(caseId, personId, cached, onDecided)

  // A candidate is acted on from the star or from a table row, and those are two
  // queries, so the face is looked up in both.
  const findCandidate = (faceId: string) =>
    [...(star.data?.candidates ?? []), ...(detail.data?.candidates ?? [])].find(
      item => item.face_id === faceId
    )

  // The overlay shows the face on its own photo, compared against this identity
  // (slice 08).
  const openFace = (faceId: string) => {
    const candidate = findCandidate(faceId)
    // No document, nothing to open: the overlay is a document view.
    if (!candidate?.document_id || !personId) return
    overlay?.open({
      documentId: candidate.document_id,
      page: candidate.page,
      faceId: candidate.face_id,
      focusPersonId: personId,
    })
  }

  const identityHref = (id: string) => identityPath(caseId, id)

  if (!personId) return null

  if (detail.isLoading || !detail.data) return <Skeleton className="h-40 w-full" />

  // No pending suggestion at all: say so instead of drawing an empty star. The
  // count is the unfiltered one, so the table's filter cannot fake it.
  const noPending = detail.data.pending_total === 0
  // As proposed, the merge folds the face's identity into this one; the dialog
  // lets the analyst swap the two (ADR-0052).
  const mergePair = decisions.mergeFaceId
    ? starMergePair(findCandidate(decisions.mergeFaceId), detail.data)
    : null
  // A viewer decides nothing about a face: no callback goes down, and the note
  // says why wherever a decision would have been offered.
  const canDecide = canDecideFaces(detail.data.member_role)
  const actions = canDecide ? decisions.actions : undefined
  const offersDecisions = !noPending || (references && detail.data.references.length > 0)

  return (
    <div className="flex flex-col gap-4">
      <FaceActionNotice
        copy={BASE}
        errorKey={decisions.failureKey}
        readOnly={!canDecide && offersDecisions}
      />
      {references ? (
        <FaceReferenceStrip
          references={detail.data.references}
          onToggleReference={canDecide ? decisions.toggleReference : undefined}
          togglingFaceId={decisions.togglingFaceId}
        />
      ) : null}
      {noPending ? (
        <p className="text-sm text-muted-foreground">{t(`${BASE}.noPending`)}</p>
      ) : (
        <>
          <PhotoMatchingStar
            candidates={star.data?.candidates ?? []}
            loading={star.isLoading}
            identity={{
              displayName: detail.data.display_name ?? t(`${BASE}.unknownIdentity`),
              photoUrl: detail.data.photo_url,
            }}
            onOpenFace={openFace}
            actions={actions}
            identityHref={identityHref}
          />
          <FaceCandidateTable
            candidates={detail.data.candidates}
            total={detail.data.total}
            page={detail.data.page}
            pageSize={detail.data.page_size ?? CANDIDATES_PAGE_SIZE}
            filter={{
              suggestedOnly: view.suggestedOnly,
              counts: {
                all: detail.data.pending_total,
                suggested: detail.data.suggested_matches,
              },
              onChange: suggestedOnly => setStored({ ...view, suggestedOnly, page: 1 }),
            }}
            onPageChange={page => setStored({ ...view, page })}
            actions={actions}
            onOpenCandidate={openFace}
            identityHref={identityHref}
          />
        </>
      )}
      {/* Outside the star and the table, so it outlives the last candidate. */}
      <MergeIdentitiesDialog
        pair={mergePair}
        onConfirm={decisions.confirmMerge}
        onClose={decisions.closeMerge}
      />
    </div>
  )
}
